// File: src/components/AdminNotifications.jsx
// Production-ready admin notification component with section filtering

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Package, TrendingDown, Star, MessageCircle, X } from 'lucide-react';
import './css/AdminNotifications.css';

const AdminNotifications = ({ 
  notifications, 
  loadingNotifications, 
  onMarkAllRead,
  onClearSection,
  className = ''
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeSection, setActiveSection] = useState('all');
  const notificationRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  // Calculate section counts
  const getSectionCount = (section) => {
    if (section === 'all') {
      return Object.values(notifications).reduce((sum, arr) => sum + arr.length, 0);
    }
    return notifications[section]?.length || 0;
  };

  const totalCount = getSectionCount('all');

  // Get section icon
  const getSectionIcon = (section) => {
    switch (section) {
      case 'orders':
        return <Package size={18} />;
      case 'products':
        return <TrendingDown size={18} />;
      case 'reviews':
        return <Star size={18} />;
      case 'chats':
        return <MessageCircle size={18} />;
      default:
        return <Bell size={18} />;
    }
  };

  // Get section title
  const getSectionTitle = (section) => {
    switch (section) {
      case 'orders':
        return 'Nouvelles commandes';
      case 'products':
        return 'Stock faible';
      case 'reviews':
        return 'Avis';
      case 'chats':
        return 'Messages';
      default:
        return 'Toutes les notifications';
    }
  };

  const getSectionLabel = (section) => {
    switch (section) {
      case 'orders':
        return 'Commandes';
      case 'products':
        return 'Stock';
      case 'reviews':
        return 'Avis';
      case 'chats':
        return 'Messages';
      default:
        return 'Tout';
    }
  };

  const getEmptyMessage = (section) => {
    switch (section) {
      case 'orders':
        return 'Aucune nouvelle commande';
      case 'products':
        return 'Aucun stock faible';
      case 'reviews':
        return 'Aucun avis';
      case 'chats':
        return 'Aucun message';
      default:
        return 'Aucune nouvelle notification';
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    navigate(notification.link);
    setShowNotifications(false);
  };

  // Handle clear section
  const handleClearSection = (section, e) => {
    e.stopPropagation();
    if (onClearSection) {
      onClearSection(section);
    }
  };

  // Get filtered notifications based on active section
  const getFilteredNotifications = () => {
    if (activeSection === 'all') {
      // Combine all sections
      return [
        ...notifications.orders,
        ...notifications.products,
        ...notifications.reviews,
        ...notifications.chats
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
    return notifications[activeSection] || [];
  };

  const filteredNotifications = getFilteredNotifications();
  const sections = ['all', 'orders', 'products', 'reviews', 'chats'];

  return (
    <div className={`notification-container ${className}`}>
      <button
        ref={buttonRef}
        onClick={() => setShowNotifications(!showNotifications)}
        className="admin-icon-button notification-button"
        aria-label="Notifications"
        disabled={loadingNotifications}
      >
        <Bell size={20} />
        {totalCount > 0 && (
          <span className="notification-badge">{totalCount > 99 ? '99+' : totalCount}</span>
        )}
      </button>

      {showNotifications && (
        <div ref={notificationRef} className="notification-dropdown">
          {/* Header */}
          <div className="notification-header">
            <div className="notification-header-left">
              <h3>Notifications</h3>
              {totalCount > 0 && (
                <span className="notification-count">{totalCount > 99 ? '99+' : totalCount}</span>
              )}
            </div>
            {totalCount > 0 && (
              <button 
                onClick={onMarkAllRead} 
                className="mark-all-read-btn"
                title="Tout marquer comme lu"
              >
                <CheckCheck size={18} />
              </button>
            )}
          </div>

          {/* Section Tabs */}
          <div className="notification-tabs">
            {sections.map((section) => {
              const count = getSectionCount(section);
              return (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`notification-tab ${activeSection === section ? 'active' : ''}`}
                  disabled={count === 0 && section !== 'all'}
                >
                  {getSectionIcon(section)}
                  <span className="tab-label">{getSectionLabel(section)}</span>
                  {count > 0 && (
                    <span className="tab-count">{count > 99 ? '99+' : count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="notification-content">
            {loadingNotifications ? (
              <div className="notification-status">
                <div className="notification-spinner"></div>
                <p>Chargement des notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="notification-status empty">
                <Bell size={40} strokeWidth={1.5} />
                <p>Tout est a jour !</p>
                <span>
                  {activeSection === 'all' 
                    ? 'Aucune nouvelle notification' 
                    : getEmptyMessage(activeSection)}
                </span>
              </div>
            ) : (
              <>
                {/* Section Header with Clear */}
                {activeSection !== 'all' && filteredNotifications.length > 0 && (
                  <div className="section-header">
                    <div className="section-title">
                      {getSectionIcon(activeSection)}
                      <span>{getSectionTitle(activeSection)}</span>
                      <span className="section-count">({filteredNotifications.length})</span>
                    </div>
                    <button 
                      onClick={(e) => handleClearSection(activeSection, e)}
                      className="clear-section-btn"
                      title="Vider la section"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                {/* Notification List */}
                <div className="notification-list">
                  {activeSection === 'all' ? (
                    // Grouped view for "all"
                    <>
                      {['orders', 'products', 'reviews', 'chats'].map((section) => {
                        const sectionItems = notifications[section] || [];
                        if (sectionItems.length === 0) return null;

                        return (
                          <div key={section} className="notification-section-group">
                            <div className="section-header">
                              <div className="section-title">
                                {getSectionIcon(section)}
                                <span>{getSectionTitle(section)}</span>
                                <span className="section-count">({sectionItems.length})</span>
                              </div>
                              <button 
                                onClick={(e) => handleClearSection(section, e)}
                                className="clear-section-btn"
                                title="Vider la section"
                              >
                                <X size={16} />
                              </button>
                            </div>
                            {sectionItems.map((notification, index) => (
                              <div
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                className="notification-item"
                                style={{ animationDelay: `${index * 0.05}s` }}
                              >
                                <div className="notification-icon">
                                  {getSectionIcon(notification.type)}
                                </div>
                                <div className="notification-content-wrapper">
                                  <p className="notification-message">{notification.message}</p>
                                  <p className="notification-details">{notification.details}</p>
                                  <p className="notification-timestamp">{notification.timestamp}</p>
                                </div>
                                <div className="notification-indicator"></div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    // Single section view
                    filteredNotifications.map((notification, index) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className="notification-item"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="notification-icon">
                          {getSectionIcon(notification.type)}
                        </div>
                        <div className="notification-content-wrapper">
                          <p className="notification-message">{notification.message}</p>
                          <p className="notification-details">{notification.details}</p>
                          <p className="notification-timestamp">{notification.timestamp}</p>
                        </div>
                        <div className="notification-indicator"></div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
